import { getMandateById } from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { KeyLabelPair } from "@/config/types";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type AppLocation = "app" | "backoffice";

export function useTopBar() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { user, lang, selectedMandateId, selectedMandate, setSelectedMandate } =
    useContext(PspContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isTablet = useIsTablet();
  const isAdmin = user?.scopes.includes("admin");
  const isSuperEmployee = user?.scopes.includes("super_employee");

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);

  // -----------------------------------------------------------------------------
  // SIDE EFFECTS
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (selectedMandateId) {
      getMandateById({ path: { mandate_id: selectedMandateId } }).then(
        (response) => {
          setSelectedMandate(response.data);
        },
      );
    } else {
      setSelectedMandate(user?.mandateRights[0]?.mandate);
    }
  }, [selectedMandateId, setSelectedMandate, user, user?.mandateRights]);

  // -----------------------------------------------------------------------------
  // EVENT HANDLERS
  // -----------------------------------------------------------------------------
  function navigateToBackoffice() {
    navigate("/admin/mandates");
  }

  function navigateToApp() {
    navigate("/home");
  }

  function navigateToAdminPanel() {
    navigate("/admin/admin-panel");
  }

  function onChangeAppLocation(appLocation: AppLocation) {
    if (appLocation === "backoffice") {
      navigateToBackoffice();
    } else {
      navigateToApp();
    }
  }

  const isInBackoffice = location.pathname.startsWith("/admin");
  const isInAdminPanel = location.pathname.startsWith("/admin/admin-panel");
  const currentAppLocation: AppLocation = isInBackoffice ? "backoffice" : "app";

  const appLocationOptions: KeyLabelPair<AppLocation>[] = [
    {
      key: "app",
      label: translate("app", lang),
    },
    {
      key: "backoffice",
      label: translate("backoffice", lang),
    },
  ];

  return {
    isInBackoffice,
    navigateToAdminPanel,
    isTablet,
    userDetailsOpen,
    setUserDetailsOpen,
    selectedMandate,
    user,
    lang,
    isInAdminPanel,
    currentAppLocation,
    onChangeAppLocation,
    appLocationOptions,
    navigateToBackoffice,
    isAdmin,
    isSuperEmployee,
  };
}
